import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LogOut, Loader2, Users, Sparkles, Download } from "lucide-react";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function downloadCsv(filename, rows, columns) {
  if (!rows.length) return;
  const escape = (v) => {
    if (v == null) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const header = columns.map((c) => c.label).join(",");
  const body = rows
    .map((r) => columns.map((c) => escape(r[c.key])).join(","))
    .join("\n");
  const csv = header + "\n" + body;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const WAITING_COLS = [
  { key: "first_name", label: "First name" },
  { key: "last_name", label: "Last name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "child_age", label: "Child age" },
  { key: "number_of_students", label: "Number of students" },
  { key: "interested_language", label: "Language" },
  { key: "learner_type", label: "Learner type" },
  { key: "preferred_start_date", label: "Preferred start" },
  { key: "created_at", label: "Registered at" },
];

const TRIAL_COLS = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "number_of_learners", label: "Learners" },
  { key: "age", label: "Age" },
  { key: "interested_language", label: "Language" },
  { key: "comments", label: "Comments" },
  { key: "created_at", label: "Booked at" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [waiting, setWaiting] = useState([]);
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === null) {
      navigate("/admin/login", { replace: true });
      return;
    }
    if (user) {
      Promise.all([
        api.get("/admin/waiting-list").then((r) => r.data),
        api.get("/admin/trial-bookings").then((r) => r.data),
      ])
        .then(([w, t]) => {
          setWaiting(w);
          setTrials(t);
        })
        .finally(() => setLoading(false));
    }
  }, [user, navigate]);

  if (user === undefined || (user && loading)) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-muted/40">
      <header className="border-b border-border bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center rounded-full bg-primary text-primary-foreground font-serif">A</div>
            <div>
              <div className="font-serif text-lg text-primary">Admin</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                AfroLingo
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-muted-foreground md:inline">
              {user?.email}
            </span>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="rounded-full"
              data-testid="admin-logout"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-6 md:grid-cols-2">
          <Stat icon={Users} label="Waiting list" value={waiting.length} tone="hsl(148 69% 13%)" />
          <Stat icon={Sparkles} label="Trial bookings" value={trials.length} tone="hsl(41 85% 55%)" />
        </div>

        <div className="mt-10">
          <Tabs defaultValue="waiting">
            <TabsList className="rounded-full bg-muted p-1">
              <TabsTrigger value="waiting" className="rounded-full" data-testid="tab-waiting">
                Waiting list ({waiting.length})
              </TabsTrigger>
              <TabsTrigger value="trials" className="rounded-full" data-testid="tab-trials">
                Trial bookings ({trials.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="waiting" className="mt-6">
              <div className="mb-4 flex justify-end">
                <Button
                  onClick={() =>
                    downloadCsv(
                      `heritage-waiting-list-${new Date().toISOString().slice(0, 10)}.csv`,
                      waiting,
                      WAITING_COLS,
                    )
                  }
                  disabled={!waiting.length}
                  variant="outline"
                  className="rounded-full"
                  data-testid="waiting-export-csv"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Export CSV
                </Button>
              </div>
              <div className="overflow-hidden rounded-3xl border border-border bg-card">
                <Table data-testid="waiting-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Language</TableHead>
                      <TableHead>Learner</TableHead>
                      <TableHead>Students</TableHead>
                      <TableHead>Start</TableHead>
                      <TableHead>Registered</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {waiting.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                          No waiting list entries yet.
                        </TableCell>
                      </TableRow>
                    )}
                    {waiting.map((r) => (
                      <TableRow key={r.id} data-testid={`waiting-row-${r.id}`}>
                        <TableCell className="font-medium">
                          {r.first_name} {r.last_name}
                        </TableCell>
                        <TableCell>{r.email}</TableCell>
                        <TableCell>{r.phone}</TableCell>
                        <TableCell>{r.interested_language}</TableCell>
                        <TableCell className="capitalize">{r.learner_type}</TableCell>
                        <TableCell>{r.number_of_students}</TableCell>
                        <TableCell>{r.preferred_start_date || "—"}</TableCell>
                        <TableCell className="text-muted-foreground">{fmt(r.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="trials" className="mt-6">
              <div className="mb-4 flex justify-end">
                <Button
                  onClick={() =>
                    downloadCsv(
                      `heritage-trial-bookings-${new Date().toISOString().slice(0, 10)}.csv`,
                      trials,
                      TRIAL_COLS,
                    )
                  }
                  disabled={!trials.length}
                  variant="outline"
                  className="rounded-full"
                  data-testid="trials-export-csv"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Export CSV
                </Button>
              </div>
              <div className="overflow-hidden rounded-3xl border border-border bg-card">
                <Table data-testid="trials-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Language</TableHead>
                      <TableHead>Learners</TableHead>
                      <TableHead>Age</TableHead>
                      <TableHead>Comments</TableHead>
                      <TableHead>Booked</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trials.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                          No trial bookings yet.
                        </TableCell>
                      </TableRow>
                    )}
                    {trials.map((r) => (
                      <TableRow key={r.id} data-testid={`trial-row-${r.id}`}>
                        <TableCell className="font-medium">{r.name}</TableCell>
                        <TableCell>{r.email}</TableCell>
                        <TableCell>{r.phone}</TableCell>
                        <TableCell>{r.interested_language}</TableCell>
                        <TableCell>{r.number_of_learners}</TableCell>
                        <TableCell>{r.age || "—"}</TableCell>
                        <TableCell className="max-w-xs truncate" title={r.comments || ""}>
                          {r.comments || "—"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{fmt(r.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-3xl border border-border bg-card p-6 shadow-sm">
      <div className="flex items-center gap-4">
        <div className="grid h-12 w-12 place-items-center rounded-2xl" style={{ background: `${tone}15` }}>
          <Icon className="h-6 w-6" style={{ color: tone }} strokeWidth={1.5} />
        </div>
        <div>
          <div className="text-sm uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="font-serif text-3xl text-primary">{value}</div>
        </div>
      </div>
    </div>
  );
}
